import {
    ButtonGroup,
    Checkbox,
    InputFieldUtils,
    IPublicRestaurant,
    StringUtils,
    TextField,
    ValidationUtils,
} from '@localina/core';
import { CompanyIcon, CookieIcon, EmailIcon, GenderIcon, MobileIcon, PersonIcon, TextBlockIcon } from '@localina/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IReservationPerson } from '../../interfaces';
import { Field } from '../Field';

interface IProps {
    person: IReservationPerson;
    restaurant: IPublicRestaurant;
    onPersonChanges: (person: IReservationPerson) => void;
}

export const validateGuestInfo = (person: IReservationPerson, restaurant: IPublicRestaurant) => {
    return ValidationUtils.validateObject(person, {
        optionalFields: ['comment', 'company', 'allergies'].filter((field) => {
            return !restaurant.requiredFieldsForReservation.includes(field);
        }),
    });
};

export const GuestInfo: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const options = [
        {
            value: 'MALE',
            label: t('summary.salutation.male'),
        },
        {
            value: 'FEMALE',
            label: t('summary.salutation.female'),
        },
        {
            value: 'OTHER',
            label: t('summary.salutation.other'),
        },
    ];

    const emailHasError = !ValidationUtils.validateField('email', props.person.email, true).isValid;
    const handlePersonChanges = (field: string) => (value: string | boolean) => {
        if (['phoneNumber', 'email'].includes(field) && typeof value == 'string') {
            value = StringUtils.removeSpace(value);
        }
        props.onPersonChanges({
            ...props.person,
            [field]: value,
        });
    };

    const getHelperText = (name: string) => {
        if (!props.restaurant.requiredFieldsForReservation.includes(name)) {
            return undefined;
        }
        return t('reservation.person.helperTexts.requiredField');
    };
    return (
        <div className="guest-info">
            <Field className="salutation" icon={<GenderIcon />} label={t('reservation.person.salutation')}>
                <ButtonGroup
                    fullWidth
                    disabled={false}
                    options={options}
                    value={props.person.salutation}
                    onChange={handlePersonChanges('salutation')}
                />
            </Field>
            <div className="name">
                <div className="widget-two">
                    <TextField
                        name="firstName"
                        autoComplete={true}
                        value={props.person.firstName}
                        required={true}
                        label={t('common.fields.firstName')}
                        icon={<PersonIcon />}
                        onChange={handlePersonChanges('firstName')}
                    />
                    <TextField
                        name="lastName"
                        autoComplete={true}
                        value={props.person.lastName}
                        required={true}
                        label={t('common.fields.lastName')}
                        onChange={handlePersonChanges('lastName')}
                    />
                </div>
                <TextField
                    name="company"
                    autoComplete={true}
                    value={props.person.company}
                    required={props.restaurant.requiredFieldsForReservation.includes('company')}
                    label={t('common.fields.company')}
                    helperText={getHelperText('company')}
                    icon={<CompanyIcon />}
                    onChange={handlePersonChanges('company')}
                />
            </div>
            <div className="contacts">
                <div className="phone-wrapper">
                    <TextField
                        name="phoneNumber"
                        autoComplete={true}
                        value={props.person.phoneNumber}
                        label={t('common.fields.phoneNumber')}
                        required
                        helperText={t('reservation.person.helperTexts.mobile')}
                        icon={<MobileIcon />}
                        onChange={handlePersonChanges('phoneNumber')}
                    />
                    {props.restaurant.subscription === 'standard' && (
                        <div className="checkbox-reminder">
                            <Checkbox
                                label={t('reservation.person.smsReminderLabel')}
                                onChange={handlePersonChanges('sendSmsReminder')}
                                checked={props.person.sendSmsReminder}
                            />
                        </div>
                    )}
                </div>
                <div className="email-wrapper">
                    <TextField
                        name="email"
                        validate={false}
                        autoComplete={true}
                        value={props.person.email}
                        required
                        label={t('common.fields.email')}
                        helperText={t('reservation.person.helperTexts.email')}
                        icon={<EmailIcon />}
                        onChange={handlePersonChanges('email')}
                        errorMessageKey={InputFieldUtils.getInvalidFormatFieldErrorMessage('email')}
                        hasError={emailHasError}
                    />
                    <div className="checkbox-reminder">
                        <Checkbox
                            label={t('reservation.person.emailReminderLabel')}
                            onChange={handlePersonChanges('sendEmailReminder')}
                            checked={props.person.sendEmailReminder}
                        />
                    </div>
                </div>
                <TextField
                    name="allergies"
                    autoComplete={true}
                    value={props.person.allergies}
                    label={t('reservation.person.allergies')}
                    multiline={true}
                    rows={2}
                    required={props.restaurant.requiredFieldsForReservation.includes('allergies')}
                    helperText={getHelperText('allergies')}
                    icon={<CookieIcon />}
                    onChange={handlePersonChanges('allergies')}
                />
                <TextField
                    name="comment"
                    autoComplete={true}
                    value={props.person.comment}
                    label={t('common.fields.comment')}
                    multiline={true}
                    rows={2}
                    required={props.restaurant.requiredFieldsForReservation.includes('comment')}
                    helperText={getHelperText('comment')}
                    icon={<TextBlockIcon />}
                    onChange={handlePersonChanges('comment')}
                />
            </div>
        </div>
    );
};
